import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import LinkIcon from '@mui/icons-material/Link';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { apiFetch, clearApiCache } from './api';
import ImportDialog from './ImportDialog';
import TournamentImportDialog from './TournamentImportDialog';
import TagManagementModal from './TagManagementModal';
import UserManagement from './UserManagement';

export default function AdminPanel({ reloadPlayers, reloadTournaments }) {
  const [importOpen, setImportOpen] = useState(false);
  const [tournamentImportOpen, setTournamentImportOpen] = useState(false);
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const [userManagementOpen, setUserManagementOpen] = useState(false);
  const [linkPlayersOpen, setLinkPlayersOpen] = useState(false);
  const [linkingPlayers, setLinkingPlayers] = useState(false);
  const [linkPlayersResult, setLinkPlayersResult] = useState(null);

  const handleLinkPlayers = async () => {
    setLinkingPlayers(true);
    try {
      const result = await apiFetch('/players-link-tournament-players', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      setLinkPlayersResult(result);
    } catch (err) {
      setLinkPlayersResult({ error: err.message || 'Verknüpfung fehlgeschlagen.' });
    } finally {
      setLinkingPlayers(false);
    }
  };

  const handleLinkPlayersClick = () => {
    setLinkPlayersOpen(true);
    setLinkPlayersResult(null);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Administration
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Spieler verwalten
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <ImportExportIcon />
              </ListItemIcon>
              <ListItemText
                primary="Meldekartei importieren"
                secondary="CSV-Datei mit Spielerdaten importieren"
              />
              <Button
                variant="contained"
                onClick={() => setImportOpen(true)}
              >
                <ImportExportIcon />
              </Button>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText
                primary="Turnierspieler verknüpfen"
                secondary="Unverknüpfte Turnierspieler mit Spielern in der Datenbank verknüpfen"
              />
              <Button
                variant="contained"
                onClick={handleLinkPlayersClick}
              >
                <LinkIcon />
              </Button>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Turniere verwalten
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <EmojiEventsIcon />
              </ListItemIcon>
              <ListItemText
                primary="Turnier importieren"
                secondary="Turnier von chess-results.com importieren"
              />
              <Button
                variant="contained"
                onClick={() => setTournamentImportOpen(true)}
              >
                <EmojiEventsIcon />
              </Button>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System verwalten
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <LocalOfferIcon />
              </ListItemIcon>
              <ListItemText
                primary="Tags verwalten"
                secondary="Spieler-Tags erstellen, bearbeiten und löschen"
              />
              <Button
                variant="contained"
                onClick={() => setTagManagementOpen(true)}
              >
                <LocalOfferIcon />
              </Button>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText
                primary="Benutzer verwalten"
                secondary="Benutzerkonten und Berechtigungen verwalten"
              />
              <Button
                variant="contained"
                onClick={() => setUserManagementOpen(true)}
              >
                <PeopleIcon />
              </Button>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={async () => {
          await clearApiCache();
          reloadPlayers();
        }}
      />

      <TournamentImportDialog
        open={tournamentImportOpen}
        onClose={() => setTournamentImportOpen(false)}
        onImported={async () => {
          await clearApiCache();
          reloadTournaments();
        }}
      />

      <TagManagementModal
        open={tagManagementOpen}
        onClose={() => setTagManagementOpen(false)}
      />

      <Dialog
        open={userManagementOpen}
        onClose={() => setUserManagementOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Benutzerverwaltung</DialogTitle>
        <DialogContent>
          <UserManagement onBack={() => setUserManagementOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog
        open={linkPlayersOpen}
        onClose={() => setLinkPlayersOpen(false)}
      >
        <DialogTitle>Turnierspieler verknüpfen</DialogTitle>
        <DialogContent>
          {!linkPlayersResult && !linkingPlayers && (
            <Typography>
              Möchten Sie unverknüpfte Turnierspieler mit bestehenden Spielern in der Datenbank verknüpfen?
            </Typography>
          )}
          {linkingPlayers && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Verknüpfe Spieler...</Typography>
            </Box>
          )}
          {linkPlayersResult && !linkPlayersResult.error && (
            <Box>
              <Typography color="success.main" sx={{ mb: 1 }}>
                ✓ {linkPlayersResult.linked} Turnierspieler verknüpft
              </Typography>
              {linkPlayersResult.still_unmatched > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {linkPlayersResult.still_unmatched} Turnierspieler konnten nicht verknüpft werden
                </Typography>
              )}
            </Box>
          )}
          {linkPlayersResult && linkPlayersResult.error && (
            <Typography color="error">{linkPlayersResult.error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!linkPlayersResult && (
            <>
              <Button onClick={() => setLinkPlayersOpen(false)} disabled={linkingPlayers}>
                Abbrechen
              </Button>
              <Button
                onClick={handleLinkPlayers}
                disabled={linkingPlayers}
                variant="contained"
                color="primary"
              >
                Verknüpfen
              </Button>
            </>
          )}
          {linkPlayersResult && (
            <Button onClick={() => setLinkPlayersOpen(false)} variant="contained">
              OK
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
